import constate from 'constate'
import { useCallback } from 'react'
import { useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import {
  PaginatedSearchState,
  PaginatedSearchStore,
  usePaginatedSearch,
} from 'scenes/Dashboard/components-next/PaginatedSearch'
import { deleteEntityFromCache, deleteEntityFromListCache } from 'utils-next/api'
import { PaginationOptions } from 'utils-next/types'

import {
  Corporate,
  CORPORATE_CACHE_STORE_ID,
  CorporateListFilters,
  getApiCorporateHandlers,
} from '../../../../../../entities-next/corporate'
import { showDeleteCorporateModal } from '../../modals'
import { failedDeleteAlertAction, successfulDeleteAlertAction } from './CorporateListViewAlerts.component'

export type CorporateListViewStore = {
  state: PaginatedSearchStore<Corporate, CorporateListViewFilters>['state']
  actions: PaginatedSearchStore<Corporate, CorporateListViewFilters>['actions'] & {
    deleteCorporate: (corporate: Corporate) => Promise<void>
  }
}

export type CorporateListViewFilters = CorporateListFilters

function useCorporateListViewInternalStore(): CorporateListViewStore {
  const { state, actions: paginatedSearchActions } = usePaginatedSearch<Corporate, CorporateListViewFilters>(
    CORPORATE_CACHE_STORE_ID,
    corporateListQuery,
    {
      initialFilters: {},
    }
  )
  const corporateListViewActions = useCorporateListViewActions(state)
  return {
    state,
    actions: {
      ...paginatedSearchActions,
      ...corporateListViewActions,
    },
  }
}

function useCorporateListViewActions(state: PaginatedSearchState<Corporate, CorporateListViewFilters>) {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const deleteCorporate = useCallback(
    async (corporate: Corporate) => {
      const { apiDeleteCorporate } = getApiCorporateHandlers()
      const shouldDelete = await showDeleteCorporateModal({ corporate })
      if (shouldDelete) {
        const queryKey = [
          CORPORATE_CACHE_STORE_ID,
          { ...state.filters },
          {
            currentPage: state.currentPage,
            limit: state.limit,
          },
        ]
        await apiDeleteCorporate(corporate.id)
          .then(() => {
            deleteEntityFromListCache(queryClient, corporate.id, queryKey)
            deleteEntityFromCache(queryClient, corporate.id, CORPORATE_CACHE_STORE_ID)
            dispatch(successfulDeleteAlertAction(corporate))
          })
          .catch(() => {
            dispatch(failedDeleteAlertAction(corporate))
          })
      }
    },
    [dispatch]
  )

  return {
    deleteCorporate,
  }
}

async function corporateListQuery(
  _key: string,
  filters: CorporateListViewFilters,
  paginationOptions: PaginationOptions
) {
  const { apiGetCorporateList } = getApiCorporateHandlers()
  const query = await apiGetCorporateList(filters, paginationOptions)
  return query.results
}

export const [CorporateListViewProvider, useCorporateListViewStore] = constate(useCorporateListViewInternalStore)
